/**
 * Plays a one-off "first download" celebration animation.
 *
 * The canvas is created on demand and removed when the loop ends, so no
 * decorative element lives in the DOM outside the animation's lifetime.
 * Removal happens once the user has dismissed the "Get the app" modal
 * (see `ModalDownloadApp.handleOnClose`, which sets
 * `localStorage.isDisplayedModalDownloadApp`). Until then the rounds keep
 * looping behind the modal.
 *
 * Single-instance: subsequent calls while a run is active are no-ops, so
 * rapid re-clicks on Download (which can land in the ~3s window before the
 * "Get the app" modal opens and the localStorage flag is eventually set)
 * cannot stack canvases or rAF loops.
 */
let isActive = false

export const displayConfetti = () => {
  if (isActive) return
  isActive = true

  const canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
  Object.assign(canvas.style, {
    background: 'transparent',
    position: 'fixed',
    inset: '0',
    zIndex: '9999',
    pointerEvents: 'none',
  })
  document.body.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    canvas.remove()
    isActive = false
    return
  }

  type Confetto = {
    color: { front: string; back: string }
    dimensions: { x: number; y: number }
    position: { x: number; y: number }
    rotation: number
    scale: { x: number; y: number }
    velocity: { x: number; y: number }
  }

  let confetti: Confetto[] = []
  const confettiCount = 300
  const gravity = 0.3
  const terminalVelocity = 2.5
  const drag = 0.075
  const colors = [
    { front: '#EDC65E', back: '#F0C269' },
    { front: '#4ABEE2', back: '#42C2D4' },
    { front: '#E8548B', back: '#DD5293' },
    { front: '#B74CDB', back: '#A457DE' },
    { front: '#6F1CB8', back: '#6424C0' },
  ]

  const randomRange = (min: number, max: number) => Math.random() * (max - min) + min

  const initConfetti = () => {
    for (let i = 0; i < confettiCount; i++) {
      confetti.push({
        color: colors[Math.floor(randomRange(0, colors.length))],
        dimensions: { x: randomRange(5, 10), y: randomRange(5, 15) },
        position: { x: randomRange(0, canvas.width), y: canvas.height - 1 },
        rotation: randomRange(0, 2 * Math.PI),
        scale: { x: 1, y: 1 },
        velocity: { x: randomRange(-25, 25), y: randomRange(0, -25) },
      })
    }
  }

  let rafId: number | null = null

  const stop = () => {
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId)
      rafId = null
    }
    canvas.remove()
    isActive = false
  }

  const render = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    confetti.forEach((confetto, index) => {
      const width = confetto.dimensions.x * confetto.scale.x
      const height = confetto.dimensions.y * confetto.scale.y
      ctx.translate(confetto.position.x, confetto.position.y)
      ctx.rotate(confetto.rotation)
      confetto.velocity.x -= confetto.velocity.x * drag
      confetto.velocity.y = Math.min(confetto.velocity.y + gravity, terminalVelocity)
      confetto.velocity.x += Math.random() > 0.5 ? Math.random() : -Math.random()
      confetto.position.x += confetto.velocity.x
      confetto.position.y += confetto.velocity.y
      if (confetto.position.y >= canvas.height) confetti.splice(index, 1)
      if (confetto.position.x > canvas.width) confetto.position.x = 0
      if (confetto.position.x < 0) confetto.position.x = canvas.width
      confetto.scale.y = Math.cos(confetto.position.y * 0.1)
      ctx.fillStyle = confetto.scale.y > 0 ? confetto.color.front : confetto.color.back
      ctx.fillRect(-width / 2, -height / 2, width, height)
      ctx.setTransform(1, 0, 0, 1, 0, 0)
    })

    if (confetti.length === 0) {
      // Once the "Get the app" modal has been dismissed (flag is set), tear
      // down. Otherwise keep firing rounds behind the modal.
      if (localStorage.getItem('isDisplayedModalDownloadApp')) {
        stop()
        return
      }
      initConfetti()
    }

    rafId = window.requestAnimationFrame(render)
  }

  initConfetti()
  render()
}
